import { gcm } from '@noble/ciphers/aes.js'
import { hkdf } from '@noble/hashes/hkdf.js'
import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex, concatBytes, utf8ToBytes } from '@noble/hashes/utils.js'

export interface ConnectionKeys {
    clientToHost: Uint8Array
    hostToClient: Uint8Array
}

export const sessionId = (tt: Uint8Array) => bytesToHex(sha256(concatBytes(utf8ToBytes('iwspos/sid/v1'), tt)).slice(0, 16))

export const connectionKeys = (ke: Uint8Array, nonceClient: Uint8Array, nonceHost: Uint8Array): ConnectionKeys => {
    const k = hkdf(sha256, ke, concatBytes(nonceClient, nonceHost), utf8ToBytes('iwspos/conn/v1'), 32)
    return { clientToHost: k.slice(0, 16), hostToClient: k.slice(16) }
}

const nonceOf = (seq: number) => {
    const n = new Uint8Array(12)
    new DataView(n.buffer).setBigUint64(4, BigInt(seq))
    return n
}

const toB64 = (b: Uint8Array) => {
    let s = ''
    for (const x of b) s += String.fromCharCode(x)
    return btoa(s)
}
const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

export interface BoxFrame {
    t: 'box'
    s: number
    d: string
}

export const seal = (key: Uint8Array, seq: number, plaintext: string): BoxFrame => ({
    t: 'box',
    s: seq,
    d: toB64(gcm(key, nonceOf(seq)).encrypt(utf8ToBytes(plaintext))),
})

export const open = (key: Uint8Array, frame: BoxFrame): string =>
    new TextDecoder().decode(gcm(key, nonceOf(frame.s)).decrypt(fromB64(frame.d)))

/** One direction of a connection: strictly increasing sequence numbers, so a replayed or reordered frame fails. */
export class Channel {
    private sendSeq = 0
    private recvSeq = 0

    constructor(
        private readonly sendKey: Uint8Array,
        private readonly recvKey: Uint8Array,
    ) {}

    seal(message: unknown): BoxFrame {
        return seal(this.sendKey, this.sendSeq++, JSON.stringify(message))
    }

    open(frame: BoxFrame): unknown {
        if (frame.s !== this.recvSeq) throw new Error('Out of sequence')
        const text = open(this.recvKey, frame)
        this.recvSeq++
        return JSON.parse(text)
    }

    static client(k: ConnectionKeys) {
        return new Channel(k.clientToHost, k.hostToClient)
    }

    static host(k: ConnectionKeys) {
        return new Channel(k.hostToClient, k.clientToHost)
    }
}
